import { useRef } from 'react';
import { useRouter } from 'next/router';
import { addMonths } from 'date-fns';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { Alert, ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  FormGroup,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { FormApi } from 'informed';
import type { Attachments } from 'shared-types';
import type { GetAchievementsRes } from 'shared-types/responses';

type FormValues = {
  completionDate?: Date;
  comments: string;
  expirationDate?: Date;
  startedAtDate?: Date;
};

const getAutomaticExpirationDate = (
  date: number,
  monthsBeforeExpiration: number
) =>
  date && monthsBeforeExpiration
    ? addMonths(new Date(date), monthsBeforeExpiration).getTime()
    : null;

interface Props {
  achievement: Pick<
    GetAchievementsRes['achievements'][0],
    | 'id'
    | 'certificateAllowAdding'
    | 'certificateRequiredFromUser'
    | 'isIncompleteViewableByUser'
    | 'monthsBeforeExpiration'
    | 'type'
    | 'userAttributes'
  >;
  isInCard?: boolean;
  isRequest: boolean;
  preventRedirectAfterSubmit?: boolean;
  updateAchievements: () => Promise<void>;
  userHasPreviousCompletions?: boolean;
}

const AchievementCompletionAddSelf = ({
  achievement,
  isInCard,
  isRequest,
  preventRedirectAfterSubmit,
  updateAchievements,
  userHasPreviousCompletions
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const { language, messages, user } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);
  const uploader = useRef<() => Promise<Attachments>>(undefined);

  const router = useRouter();

  const handleCompletionDateChange = (value: number) => {
    if (achievement.monthsBeforeExpiration) {
      formApi.current?.setValue(
        'expirationDate',
        addMonths(new Date(value), achievement.monthsBeforeExpiration).getTime()
      );
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const attachments = achievement.certificateAllowAdding
        ? await uploader.current()
        : null;

      const userAttributes = {};

      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          userAttributes[attributeId] = values[fieldKey];
        }
      });

      if (isRequest) {
        await api.post(`/admin/achievements/${achievement.id}/request-access`, {
          attachments,
          comments: values.comments,
          completionDate: values.completionDate,
          expirationDate: values.expirationDate,
          startedAtDate: values.startedAtDate,
          userAttributes
        });

        dispatch.addNotice({
          text: messages.profileDeleteRequestSuccess,
          type: 'success'
        });

        await updateAchievements();
      } else {
        await api.post(`/admin/achievements/${achievement.id}/completions`, {
          achievementCompletions: [
            {
              attachments,
              comments: values.comments,
              completionDate: values.completionDate,
              expirationDate: values.expirationDate,
              startedAtDate: values.startedAtDate,
              status: ['external', 'milestone'].includes(achievement.type)
                ? 'completed'
                : 'inprogress',
              userAttributes,
              userId: user.id,
              ...(user.email
                ? { email: user.email }
                : { username: user.username })
            }
          ]
        });

        dispatch.addNotice({
          text: messages.achievementUsersAddSuccess,
          type: 'success'
        });

        if (
          !preventRedirectAfterSubmit &&
          (!['tasklist', 'conversation'].includes(achievement.type) ||
            achievement.isIncompleteViewableByUser)
        ) {
          router.push(`/achievements/${achievement.id}`);
        } else {
          await updateAchievements();
        }
      }
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const actionText = userHasPreviousCompletions
    ? isRequest
      ? messages.commonRequestNewCompletion
      : messages.commonAddNewCompletion
    : isRequest
    ? ['conversation', 'tasklist'].includes(achievement.type)
      ? messages.commonRequestToJoin
      : messages.achievementRequest
    : achievement.type === 'conversation'
    ? messages.achievementJoinConversationNotes
    : achievement.type === 'tasklist'
    ? messages.achievementJoinTaskList
    : messages.achievementAdd;

  return !user ? null : (
    <ModalButtonWithForm
      buttonCategory={userHasPreviousCompletions ? 'light' : 'default'}
      buttonProps={{ isRounded: true }}
      buttonText={actionText}
      initialValues={{
        completionDate: now,
        expirationDate: getAutomaticExpirationDate(
          now,
          achievement.monthsBeforeExpiration
        )
      }}
      modalActionCategory="success"
      modalActionText={
        isRequest
          ? ['conversation', 'tasklist'].includes(achievement.type)
            ? messages.commonRequestToJoin
            : messages.achievementRequest
          : ['conversation', 'tasklist'].includes(achievement.type)
          ? messages.commonJoin
          : messages.achievementAdd
      }
      modalAutoFocus={false} // Disable auto focus on the first input field since it's an optional datepicker
      modalTitle={actionText}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
      stopPropagation={true}
      {...(isInCard
        ? {
            buttonSize: 'small',
            buttonStyle: { fontSize: '1.3rem', padding: '6px 12px 7px' }
          }
        : {})}
    >
      {({ values }: { values: FormValues }) => (
        <div className="inner">
          {userHasPreviousCompletions ? (
            <Alert
              style={{ marginBottom: 30 }}
              text={messages.achievementAddArchiveInfoAlt}
            />
          ) : null}

          {['external', 'milestone'].includes(achievement.type) ? (
            <div className="dates">
              {achievement.type === 'external' ? (
                <FormGroup
                  label={messages.commonStartDate}
                  labelIsOptional={true}
                >
                  <Datepicker
                    field="startedAtDate"
                    isClearable={true}
                    placeholder={messages.publishStartDateEmpty}
                    maxDate={
                      values.completionDate || values.expirationDate || null
                    }
                    simpleClear={true}
                  />
                </FormGroup>
              ) : null}

              <FormGroup
                label={messages.excelCompletionDate}
                style={{ margin: 0 }}
              >
                <Datepicker
                  field="completionDate"
                  isClearable={false}
                  onChange={handleCompletionDateChange}
                  placeholder={messages.publishStartDateEmpty}
                  maxDate={values.expirationDate || null}
                  minDate={values.startedAtDate || null}
                  simpleClear={true}
                />
              </FormGroup>

              {achievement.type === 'external' ? (
                <FormGroup
                  label={messages.commonExpirationDate}
                  labelIsOptional={!achievement.monthsBeforeExpiration}
                  style={{ margin: 0 }}
                >
                  <div
                    {...(achievement.monthsBeforeExpiration && {
                      'aria-label':
                        messages.achievementCompletionAutomaticExpirationTooltip,
                      'data-tooltip':
                        messages.achievementCompletionAutomaticExpirationTooltip
                    })}
                  >
                    <Datepicker
                      disabled={achievement.monthsBeforeExpiration}
                      field="expirationDate"
                      isClearable={!Boolean(achievement.monthsBeforeExpiration)}
                      placeholder={messages.publishEndDateEmpty}
                      minDate={
                        values.completionDate || values.startedAtDate || null
                      }
                      simpleClear={true}
                    />
                  </div>
                </FormGroup>
              ) : null}
            </div>
          ) : null}

          <FormGroup
            htmlFor="achievement-add-self-comments-textarea"
            label={messages.commonComment}
            labelIsOptional={true}
          >
            <TextArea
              autoGrow={true}
              field="comments"
              id="achievement-add-self-comments-textarea"
              rows={3}
              style={{ minWidth: 200 }}
            />
          </FormGroup>

          {achievement.certificateAllowAdding && (
            <MultipleAttachments
              getUploadHandler={(fn) => (uploader.current = fn)}
              required={achievement.certificateRequiredFromUser}
            />
          )}

          {achievement.userAttributes?.map((attribute) => (
            <FormGroup
              key={attribute.id}
              label={attribute.titleTranslations?.[language] || attribute.title}
              labelIsOptional={!attribute.isRequired}
            >
              <ProductUserCustomAttributeRenderFormField
                attribute={attribute}
              />
            </FormGroup>
          ))}

          <style jsx>{`
            .dates {
              align-items: flex-start;
              display: flex;
              flex-direction: column;
              gap: 15px 30px;
            }
            @media (min-width: ${breakpoints.medium}px) {
              .dates {
                flex-direction: row;
              }
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementCompletionAddSelf;
